import { Chip } from '@heroui/react'

import { RegisterColumns, ReportSaleKind } from '../types'
import type { ReportMoney, SalesRegisterRow, SalesRegisterTotal } from '../types'
import { settledAt } from './report-format'
import type { ReportColumn } from './report-table'

/** A register line, or the period's total dressed as one. */
export type RegisterLine = Partial<SalesRegisterRow> &
  Partial<SalesRegisterTotal> &
  ReportMoney & { isTotal?: boolean }

const STATUS_LABELS: Record<ReportSaleKind, string> = {
  [ReportSaleKind.Sale]: 'Sale',
  [ReportSaleKind.Refund]: 'Refund',
}

/**
 * In the legacy "Sales Register (Summary All Columns)" order and wording.
 * NexQR charges no specific tax and no service charge, so those two print as
 * zeros — they are kept so the sheet lines up column for column with the old
 * one — and the delivery fee, which the legacy had no column for, follows the
 * service charge.
 */
const COLUMNS: ReportColumn<RegisterLine>[] = [
  { id: 'at', header: 'Date', value: (row) => settledAt(row.at) },
  {
    id: 'invoice',
    header: 'Invoice No.',
    // Orders from before invoice numbering carry only the till's reference.
    value: (row) => row.invoiceNumber ?? row.orderNumber,
    render: (row) => (
      <span className="flex items-center gap-2">
        <span className="tabular-nums">{row.invoiceNumber ?? row.orderNumber}</span>
        {/* Refunds are listed where they happened and kept out of every total.
            The chip is what tells the two kinds of line apart at a glance,
            since a refund's figures are positive like a sale's. */}
        {row.kind === ReportSaleKind.Refund && (
          <Chip size="sm" variant="soft" color="warning">
            Refund
          </Chip>
        )}
      </span>
    ),
  },
  { id: 'customer', header: 'Customer Name', value: (row) => row.customerName },
  { id: 'cashier', header: 'Employee Name', value: (row) => row.cashierName, hideWhenEmpty: true },
  {
    id: 'branch',
    header: 'Location',
    value: (row) => row.branchName,
    visibleWhen: (rows) => new Set(rows.map((row) => row.branchName).filter(Boolean)).size > 1,
  },
  { id: 'table', header: 'Table', value: (row) => row.tableName },
  { id: 'shift', header: 'Shift No.', value: (row) => row.shiftNumber },
  { id: 'total', header: 'Total Amount', format: 'usd', value: (row) => row.totalAmount },
  {
    id: 'discount',
    header: 'Discount',
    format: 'usd',
    value: (row) => row.discount,
    hideWhenEmpty: true,
  },
  {
    id: 'afterDiscount',
    header: 'Total Amount After Discount',
    format: 'usd',
    value: (row) => row.afterDiscount,
  },
  { id: 'specificTax', header: 'Specific Tax', format: 'usd', value: () => 0 },
  { id: 'vat', header: 'VAT', format: 'usd', value: (row) => row.vat, hideWhenEmpty: true },
  { id: 'serviceCharge', header: 'Service Charge', format: 'usd', value: () => 0 },
  {
    id: 'delivery',
    header: 'Delivery',
    format: 'usd',
    value: (row) => row.delivery,
    hideWhenEmpty: true,
  },
  { id: 'net', header: 'Net Amount', format: 'usd', value: (row) => row.net },
  { id: 'received', header: 'Total Receive', format: 'usd', value: (row) => row.totalReceived },
  { id: 'change', header: 'Change', format: 'usd', value: (row) => row.changeGiven },
  {
    id: 'status',
    header: 'Status',
    value: (row) => (row.isTotal || !row.kind ? null : STATUS_LABELS[row.kind]),
  },
]

/** The columns the short register — `RegisterColumns.Summary` — keeps. */
const SUMMARY_COLUMNS = new Set([
  'at',
  'invoice',
  'customer',
  'cashier',
  'branch',
  'total',
  'discount',
  'net',
])

/** The columns a register prints in this view, less the one its blocks are named by. */
export function visibleColumns(columns: RegisterColumns, omit: string[]): ReportColumn<RegisterLine>[] {
  return COLUMNS
    .filter(
      (column) =>
        !omit.includes(column.id) &&
        (columns === RegisterColumns.All || SUMMARY_COLUMNS.has(column.id)),
    )
    .map((column) =>
      // All columns is the legacy sheet, printed as it was: every column on
      // every page, zeros included, so it can be laid against the old one.
      columns === RegisterColumns.All
        ? { ...column, hideWhenEmpty: false, visibleWhen: undefined }
        : column,
    )
}
